import { createEntityAdapter } from '@ngrx/entity';
import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import { AssetTypeEnum, EntityId, Instrument, LoadingStatus, PricePeriod, PriceSeries } from '@qvs/core';
import * as fromActions from './instrument.actions';
import { onNestedLoadingStatus } from '~/shared/helpers/loading-status-helper';
import { immerOn } from 'ngrx-immer/store';
import { InstrumentNotPresentError } from '~/modules/instrument/error/InstrumentNotPresentError';
import { isinBcMap } from '~/modules/instrument/isinBcMap';
import { bcMarketplaceMap } from '~/modules/instrument/bcMarketplaceMap';
import { merge } from 'lodash-es';

const instrumentEntityAdapter = createEntityAdapter<Instrument>();

export const initialState = instrumentEntityAdapter.getInitialState({
  instrumentList: {
    loadingStatus: LoadingStatus.IDLE
  },
  watchlist: {
    currentlyWatchedIds: new Set<EntityId>(),
    loadingStatus: LoadingStatus.IDLE,
    addingStatus: LoadingStatus.IDLE,
    removingStatus: LoadingStatus.IDLE
  },
  details: {
    currentInstrumentId: undefined as EntityId,
    statusPerInstrument: new Map<
      EntityId,
      {
        details: LoadingStatus;
        six: LoadingStatus;
        lastPrice: LoadingStatus;
      }
    >()
  },
  // TODO 28-Jun-2023/braune: in case we implement prefetching, one loadingStatus per period won't suffice
  prices: {
    p1d: LoadingStatus.IDLE,
    p1w: LoadingStatus.IDLE,
    p1m: LoadingStatus.IDLE,
    p3m: LoadingStatus.IDLE,
    p6m: LoadingStatus.IDLE,
    p1y: LoadingStatus.IDLE,
    p5y: LoadingStatus.IDLE
  } as Record<PricePeriod, LoadingStatus>
});

export type State = typeof initialState;

export const instrumentFeatureKey = 'instruments';

const reducer = createReducer(
  initialState,
  on(fromActions.updateInstruments, (state, { updates }) => instrumentEntityAdapter.updateMany(updates, state)),
  immerOn(fromActions.updateInstrument, (state, instrument) => {
    if (state.entities[instrument.id]) {
      state.entities[instrument.id] = merge({ ...state.entities[instrument.id] }, instrument.changes);
    }
  }),
  on(fromActions.upsertInstruments, (state, { entities }) => instrumentEntityAdapter.upsertMany(entities, state)),
  on(fromActions.upsertInstrument, (state, { entity }) => instrumentEntityAdapter.upsertOne(entity, state)),
  immerOn<State, [typeof fromActions.loadInstrumentListSuccess]>(fromActions.loadInstrumentListSuccess, (state) => {
    state.instrumentList.loadingStatus = LoadingStatus.SUCCEEDED;
  }),
  ...onNestedLoadingStatus<State>(fromActions.loadInstrumentList, fromActions.loadInstrumentListFailure, 'instrumentList'),
  immerOn<State, [typeof fromActions.loadInstrumentDetails]>(fromActions.loadInstrumentDetails, (state, { instrumentId }) => {
    state.details.statusPerInstrument.set(instrumentId, {
      details: LoadingStatus.LOADING,
      six: state.details.statusPerInstrument.get(instrumentId)?.six ?? LoadingStatus.IDLE,
      lastPrice: state.details.statusPerInstrument.get(instrumentId)?.lastPrice ?? LoadingStatus.IDLE
    });
  }),
  immerOn<State, [typeof fromActions.loadInstrumentDetailsSuccess]>(
    fromActions.loadInstrumentDetailsSuccess,
    (state, { instrumentId }) => {
      state.details.statusPerInstrument.set(instrumentId, {
        details: LoadingStatus.SUCCEEDED,
        six: state.details.statusPerInstrument.get(instrumentId)?.six ?? LoadingStatus.IDLE,
        lastPrice: state.details.statusPerInstrument.get(instrumentId)?.lastPrice ?? LoadingStatus.IDLE
      });
    }
  ),
  immerOn<State, [typeof fromActions.loadInstrumentDetailsFailure]>(
    fromActions.loadInstrumentDetailsFailure,
    (state, { instrumentId }) => {
      state.details.statusPerInstrument.set(instrumentId, {
        details: LoadingStatus.FAILED,
        six: state.details.statusPerInstrument.get(instrumentId)?.six ?? LoadingStatus.IDLE,
        lastPrice: state.details.statusPerInstrument.get(instrumentId)?.lastPrice ?? LoadingStatus.IDLE
      });
    }
  ),
  immerOn<State, [typeof fromActions.loadInstrumentSixDetails]>(fromActions.loadInstrumentSixDetails, (state, { instrument }) => {
    state.details.statusPerInstrument.set(instrument.id, {
      details: state.details.statusPerInstrument.get(instrument.id)?.details ?? LoadingStatus.IDLE,
      six: LoadingStatus.LOADING,
      lastPrice: state.details.statusPerInstrument.get(instrument.id)?.lastPrice ?? LoadingStatus.IDLE
    });
  }),
  immerOn<State, [typeof fromActions.loadInstrumentSixDetailsSuccess]>(
    fromActions.loadInstrumentSixDetailsSuccess,
    (state, { instrument }) => {
      state.details.statusPerInstrument.set(instrument.id, {
        details: state.details.statusPerInstrument.get(instrument.id)?.details ?? LoadingStatus.IDLE,
        six: LoadingStatus.SUCCEEDED,
        lastPrice: state.details.statusPerInstrument.get(instrument.id)?.lastPrice ?? LoadingStatus.IDLE
      });
    }
  ),
  immerOn<State, [typeof fromActions.loadInstrumentSixDetailsFailure]>(
    fromActions.loadInstrumentSixDetailsFailure,
    (state, { instrument }) => {
      state.details.statusPerInstrument.set(instrument.id, {
        details: state.details.statusPerInstrument.get(instrument.id)?.details ?? LoadingStatus.IDLE,
        six: LoadingStatus.FAILED,
        lastPrice: state.details.statusPerInstrument.get(instrument.id)?.lastPrice ?? LoadingStatus.IDLE
      });
    }
  ),
  immerOn<State, [typeof fromActions.loadInstrumentLastPriceList]>(
    fromActions.loadInstrumentLastPriceList,
    (state, { instruments }) => {
      instruments.forEach((instrument) => {
        state.details.statusPerInstrument.set(instrument.id, {
          details: state.details.statusPerInstrument.get(instrument.id)?.details ?? LoadingStatus.IDLE,
          six: state.details.statusPerInstrument.get(instrument.id)?.six ?? LoadingStatus.IDLE,
          lastPrice: LoadingStatus.LOADING
        });
      });
    }
  ),
  immerOn<State, [typeof fromActions.loadInstrumentLastPriceListSuccess]>(
    fromActions.loadInstrumentLastPriceListSuccess,
    (state, { instruments }) => {
      instruments.forEach((instrument) => {
        state.details.statusPerInstrument.set(instrument.id, {
          details: state.details.statusPerInstrument.get(instrument.id)?.details ?? LoadingStatus.IDLE,
          six: state.details.statusPerInstrument.get(instrument.id)?.six ?? LoadingStatus.IDLE,
          lastPrice: LoadingStatus.SUCCEEDED
        });
      });
    }
  ),
  immerOn<State, [typeof fromActions.loadInstrumentLastPriceListFailure]>(
    fromActions.loadInstrumentLastPriceListFailure,
    (state, { instruments }) => {
      instruments.forEach((instrument) => {
        state.details.statusPerInstrument.set(instrument.id, {
          details: state.details.statusPerInstrument.get(instrument.id)?.details ?? LoadingStatus.IDLE,
          six: state.details.statusPerInstrument.get(instrument.id)?.six ?? LoadingStatus.IDLE,
          lastPrice: LoadingStatus.FAILED
        });
      });
    }
  ),
  immerOn<State, [typeof fromActions.loadWatchlistSuccess]>(
    fromActions.loadWatchlistSuccess,
    (state, { response: { currentlyWatchedIds } }) => {
      state.watchlist.currentlyWatchedIds = new Set(currentlyWatchedIds);
      state.watchlist.loadingStatus = LoadingStatus.SUCCEEDED;
    }
  ),
  ...onNestedLoadingStatus<State>(fromActions.loadWatchlist, fromActions.loadWatchlistFailure, 'watchlist'),
  immerOn<State, [typeof fromActions.addToWatchlist]>(fromActions.addToWatchlist, (state) => {
    state.watchlist.addingStatus = LoadingStatus.LOADING;
  }),
  immerOn<State, [typeof fromActions.addToWatchlistSuccess]>(
    fromActions.addToWatchlistSuccess,
    (state, { instrumentId, watchlistId }) => {
      const instrument = state.entities[instrumentId];
      if (instrument) {
        instrument.watchlistId = watchlistId;
        state.watchlist.currentlyWatchedIds.add(instrumentId);
        state.watchlist.addingStatus = LoadingStatus.SUCCEEDED;
      } else {
        state.watchlist.addingStatus = LoadingStatus.FAILED;
      }
    }
  ),
  immerOn<State, [typeof fromActions.addToWatchlistFailure]>(fromActions.addToWatchlistFailure, (state) => {
    state.watchlist.addingStatus = LoadingStatus.FAILED;
  }),
  immerOn<State, [typeof fromActions.removeFromWatchlist]>(fromActions.removeFromWatchlist, (state) => {
    state.watchlist.removingStatus = LoadingStatus.LOADING;
  }),
  immerOn<State, [typeof fromActions.removeFromWatchlistSuccess]>(
    fromActions.removeFromWatchlistSuccess,
    (state, { instrumentId }) => {
      state.entities[instrumentId].watchlistId = undefined;
      state.watchlist.currentlyWatchedIds.delete(instrumentId);
      state.watchlist.removingStatus = LoadingStatus.SUCCEEDED;
    }
  ),
  immerOn<State, [typeof fromActions.removeFromWatchlistFailure]>(fromActions.removeFromWatchlistFailure, (state) => {
    state.watchlist.removingStatus = LoadingStatus.FAILED;
  }),
  immerOn<State, [typeof fromActions.loadPrices]>(fromActions.loadPrices, (state, action) => {
    state.prices[action.period.toLowerCase()] = LoadingStatus.LOADING;
  }),
  immerOn<State, [typeof fromActions.loadPricesSuccess]>(
    fromActions.loadPricesSuccess,
    (state, { instrumentId, period, priceSeries }) => {
      state.prices[period.toLowerCase()] = LoadingStatus.SUCCEEDED;
      if (!state.entities[instrumentId]) {
        throw new InstrumentNotPresentError();
      } else if (!state.entities[instrumentId].priceData) {
        state.entities[instrumentId].priceData = {};
      }
      state.entities[instrumentId].priceData[period.toLowerCase()] = priceSeries;
    }
  ),
  immerOn<State, [typeof fromActions.loadIntraDayPricesSuccess]>(fromActions.loadIntraDayPricesSuccess, (state) => {
    state.prices.p1d = LoadingStatus.SUCCEEDED;
  }),
  immerOn<State, [typeof fromActions.loadPricesFailure]>(fromActions.loadPricesFailure, (state, { period }) => {
    state.prices[period.toLowerCase()] = LoadingStatus.FAILED;
  }),
  immerOn<State, [typeof fromActions.setCurrentInstrumentId]>(fromActions.setCurrentInstrumentId, (state, { instrumentId }) => {
    state.details.currentInstrumentId = instrumentId;
  })
);

export const instrumentFeature = createFeature({
  name: instrumentFeatureKey,
  reducer
});

const { selectInstrumentsState, selectEntities, selectDetails, selectWatchlist, selectPrices, selectInstrumentList } =
  instrumentFeature;

export const selectInstrumentEntities = selectEntities;

export const selectStatusPerInstrument = createSelector(selectDetails, (details) => details.statusPerInstrument);

export const selectAllInstruments = createSelector(selectInstrumentsState, instrumentEntityAdapter.getSelectors().selectAll);

// watch list

const selectCurrentlyWatchedIds = createSelector(selectWatchlist, (watchlist) => watchlist.currentlyWatchedIds);

// TODO 13-Jul-2023/braune: sort by name?
export const selectWatchlistInstruments = createSelector(
  selectEntities,
  selectCurrentlyWatchedIds,
  (instruments, currentlyWatchedIds) => Array.from(currentlyWatchedIds).map((id) => instruments[id]) ?? ([] as Instrument[])
);

// instrument details

export const selectCurrentInstrumentId = createSelector(selectDetails, (details) => details.currentInstrumentId);

export const selectCurrentInstrument = createSelector(
  selectEntities,
  selectCurrentInstrumentId,
  (instruments, currentInstrumentId) => instruments[currentInstrumentId] ?? ({} as Instrument)
);

export const selectCurrentInstrumentWithMarketplace = createSelector(selectCurrentInstrument, (instrument) => ({
  ...instrument,
  marketplace: {
    ...bcMarketplaceMap.get(isinBcMap.get(instrument.isin))
  }
}));

export const selectIsCurrentInstrumentMutualFund = createSelector(
  selectCurrentInstrument,
  (instrument) => instrument.assetType === AssetTypeEnum.MUTUAL_FUND
);

// instrument by id

export const selectSecurityById = (securityId: EntityId) =>
  createSelector(selectEntities, (instruments) => instruments[securityId] ?? ({} as Instrument));

// prices

const selectAllPricesLoaded = createSelector(selectPrices, (pricePeriods) =>
  (Object.keys(pricePeriods) as PricePeriod[]).every(
    (period) => pricePeriods[period] === LoadingStatus.SUCCEEDED || pricePeriods[period] === LoadingStatus.FAILED
  )
);

export const selectAvailablePricePeriods = createSelector(
  selectPrices,
  selectCurrentInstrument,
  selectAllPricesLoaded,
  (pricePeriods, currentInstrument, allPricesLoaded) =>
    allPricesLoaded
      ? (Object.keys(pricePeriods) as PricePeriod[]).filter(
          (period) =>
            pricePeriods[period] === LoadingStatus.SUCCEEDED && currentInstrument?.priceData?.[period]?.prices.length > 0
        )
      : []
);

export const selectPricesForPeriod = (period: PricePeriod) =>
  createSelector(selectCurrentInstrument, (currentInstrument) => currentInstrument?.priceData?.[period] ?? ({} as PriceSeries));

export const selectInstrumentSixLoadingStatus = (instrumentId: EntityId) =>
  createSelector(selectDetails, (details) => details.statusPerInstrument.get(instrumentId).six);

export const selectInstrumentLastPriceLoadingStatus = (instrumentId: EntityId) =>
  createSelector(selectDetails, (details) => details.statusPerInstrument.get(instrumentId).lastPrice);

export const selectIsInstrumentListLoading = createSelector(
  selectInstrumentList,
  (instrumentList) => instrumentList.loadingStatus === LoadingStatus.LOADING
);

export const selectPricesLoading = createSelector(selectPrices, (prices) =>
  Object.values(prices).some((status) => status === LoadingStatus.LOADING)
);
